/**
 * coachWait.ts — "start this once the page is ready for it".
 *
 * An automatic tour or a nudge must not begin while a real modal is open
 * (the post-onboarding welcome on /templates) or before the element it
 * points at has mounted (the workspace panes arrive after the task loads).
 * Polling a predicate is the honest way to wait for both: neither React
 * mount nor a modal close emits a DOM event we could listen for, and a
 * MutationObserver over the whole body fires on every keystroke.
 *
 * `waitUntil` runs `cb` synchronously when the predicate already holds —
 * so the common case (manual start on a settled page) has no delay at all.
 * Otherwise it polls; past `timeoutMs` it gives up silently. The returned
 * function cancels.
 */

export interface WaitUntilOptions {
  timeoutMs?: number;
  pollMs?: number;
  /** Injectable clock for tests. */
  now?: () => number;
}

export function waitUntil(
  predicate: () => boolean,
  cb: () => void,
  opts: WaitUntilOptions = {},
): () => void {
  const timeoutMs = opts.timeoutMs ?? 8000;
  const pollMs = opts.pollMs ?? 150;
  const now = opts.now ?? (() => Date.now());

  let ok = false;
  try {
    ok = predicate();
  } catch {
    ok = false;
  }
  if (ok) {
    cb();
    return () => {};
  }

  const deadline = now() + timeoutMs;
  let handle: ReturnType<typeof setInterval> | null = setInterval(() => {
    let ready = false;
    try {
      ready = predicate();
    } catch {
      ready = false;
    }
    if (ready) {
      stop();
      cb();
      return;
    }
    if (now() >= deadline) stop();
  }, pollMs);

  function stop() {
    if (handle != null) {
      clearInterval(handle);
      handle = null;
    }
  }

  return stop;
}
